import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, User, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function StudentRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [form, setForm] = useState({
    name: '', fatherName: '', dob: '', collegeName: '',
    locality: '', phone: '', email: '', address: '',
    password: '', confirmPassword: '', photo: null
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.dob) e.dob = 'Date of birth is required';
    if (!form.collegeName.trim()) e.collegeName = 'College name is required';
    if (!form.locality.trim()) e.locality = 'Locality is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit Indian mobile number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      if (form.fatherName.trim()) fd.append('fatherName', form.fatherName.trim());
      fd.append('dob', form.dob);
      fd.append('collegeName', form.collegeName.trim());
      fd.append('locality', form.locality.trim());
      fd.append('phone', form.phone.trim());
      if (form.email.trim()) fd.append('email', form.email.trim());
      if (form.address.trim()) fd.append('address', form.address.trim());
      fd.append('password', form.password);
      if (form.photo) fd.append('photo', form.photo);

      await api.post('/auth/student/register', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setRegisteredEmail(form.email.trim() || form.phone.trim());
      setStep('success');
      toast.success('Registration successful! Wait for admin approval.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed';
      toast.error(msg);
      if (msg.toLowerCase().includes('email')) {
        setErrors({ email: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const inputClass = (field) =>
    `w-full px-3 py-2.5 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 ${
      errors[field] ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-blue-400'
    }`;

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-10 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center space-y-5">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full border border-green-100 mx-auto">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">Registration Submitted!</h1>
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
              Your registration is <strong>pending admin approval</strong>. Once approved, you will receive your Hall Ticket Number and password.
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 text-left space-y-1.5">
            <p className="font-semibold">What happens next?</p>
            <p>1. Admin will review your registration</p>
            <p>2. Hall Ticket Number & Password will be generated</p>
            <p>3. Use them to log in at the Exam Login page</p>
          </div>
          {registeredEmail && (
            <p className="text-xs text-gray-400">Reference: {registeredEmail}</p>
          )}
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Back to Home
        </button>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full border border-blue-100 mx-auto mb-3">
              <User size={24} className="text-blue-500" />
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900">Student Registration</h1>
            <p className="text-sm text-gray-500 mt-1">Fill in your details to register for the exam</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Identity */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Identity</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" className={inputClass('name')} placeholder="e.g. Arjun Reddy"
                    value={form.name} onChange={e => update('name', e.target.value)} />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                  <input type="text" className={inputClass('fatherName')} placeholder="Father's name"
                    value={form.fatherName} onChange={e => update('fatherName', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <input type="date" className={inputClass('dob')} max={new Date().toISOString().split('T')[0]}
                    value={form.dob} onChange={e => update('dob', e.target.value)} />
                  {errors.dob && <p className="text-xs text-red-500 mt-1">{errors.dob}</p>}
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="tel" className={inputClass('phone')} placeholder="9876543210" maxLength={10}
                    value={form.phone} onChange={e => update('phone', e.target.value.replace(/\D/g, ''))} />
                  {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input type="email" className={inputClass('email')} placeholder="student@example.com"
                    value={form.email} onChange={e => update('email', e.target.value)} />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" className={inputClass('address')} placeholder="Residential address"
                    value={form.address} onChange={e => update('address', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Academic */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Academic</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">College / Institute *</label>
                  <input type="text" className={inputClass('collegeName')} placeholder="e.g. BITS Vizag"
                    value={form.collegeName} onChange={e => update('collegeName', e.target.value)} />
                  {errors.collegeName && <p className="text-xs text-red-500 mt-1">{errors.collegeName}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Locality *</label>
                  <input type="text" className={inputClass('locality')} placeholder="e.g. Vizag"
                    value={form.locality} onChange={e => update('locality', e.target.value)} />
                  {errors.locality && <p className="text-xs text-red-500 mt-1">{errors.locality}</p>}
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Password</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" className={inputClass('password')} placeholder="Min 6 characters"
                    value={form.password} onChange={e => update('password', e.target.value)} />
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <input type="password" className={inputClass('confirmPassword')} placeholder="Re-enter password"
                    value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} />
                  {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>

            {/* Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Photo <span className="text-gray-400 font-normal">(optional)</span></label>
              <input type="file" accept="image/*" className="input-field text-sm py-1.5"
                onChange={e => setForm(prev => ({ ...prev, photo: e.target.files[0] }))} />
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-bold text-white bg-black hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
